import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ExperimentalPendingTasks,
  inject,
  Inject,
  Optional,
} from '@angular/core';
import { RouterModule } from '@angular/router';
import { MetaData } from 'src/app/core/interfaces/Contentful/MetaData/MetaData';
import { ContentfulRenderService, MetaTagsService } from 'src/app/shared/services';
import { PAGE_DATA } from 'src/types/tokens/inject-tokens';
import { ContentfulCloudinaryImage } from '../../core/interfaces/Contentful/CloudinaryImage/ContentfulCloudinaryImage';

import { NgClass } from '@angular/common';
import { AppContentfulArticleBreadcrumbComponent } from 'src/app/shared/components/contentful/contentful-article-breadcrumbs/contentful-article-breadcrumbs.component';
import { AppContentfulBiographyComponent } from 'src/app/shared/components/contentful/contentful-biography/contentful-biography.component';
import { AppContentfulCloudinaryImageComponent } from 'src/app/shared/components/contentful/contentful-cloudinary-image/contentful-cloudinary-image.component';
import { AppContentfulDescriptionComponent } from 'src/app/shared/components/contentful/contentful-description/contentful-description.component';
import { AppContentfulFAQLandingComponent } from 'src/app/shared/components/contentful/contentful-faq-landing/contentful-faq-landing.component';
import { AppContentfulFAQComponent } from 'src/app/shared/components/contentful/contentful-faq/contentful-faq.component';
import { AppContentfulLandingPageComponent } from 'src/app/shared/components/contentful/contentful-landing-page/contentful-landing-page.component';
import { AppContentfulMiniNavigationComponent } from 'src/app/shared/components/contentful/contentful-mini-navigation/contentful-mini-navigation.component';
import { AppContentfulRelatedArticlesComponent } from 'src/app/shared/components/contentful/contentful-related-articles/contentful-related-articles.component';
import { AppContentfulRelatedProductsComponent } from 'src/app/shared/components/contentful/contentful-related-products/contentful-related-products.component';
import { AppContentfulRichTextBlockComponent } from 'src/app/shared/components/contentful/contentful-rich-text-block/contentful-rich-text-block.component';
import { AppContentfulShareArticleComponent } from 'src/app/shared/components/contentful/contentful-share-article/contentful-share-article.component';
import { AppContentfulTitleComponent } from 'src/app/shared/components/contentful/contentful-title/contentful-title.component';
import { GenericLogger } from '@scalefast/ecommerce-core';
import { ContentfulFAQLandingPageContent } from 'src/app/core/interfaces/Contentful/Page/ContentfulFAQLandingPageContent';
import { ContentfulCategoryContent } from 'src/app/core/interfaces/Contentful/Category/ContentfulCategoryContent';
import { ContentfulSectionContent } from 'src/app/core/interfaces/Contentful/ContentfulSectionContent';
import { ContentfulPage } from 'src/app/core/interfaces/Page/page-contentful.interface';
import { isContentfulPage } from 'src/app/shared/page-provider';
import { BasePage } from '@scalefast/core';

@Component({
  selector: 'app-contentful',
  templateUrl: './contentful-page.component.html',
  styleUrls: ['../../../scss/pages/contentful.scss', '../../../scss/pages/faq.scss', './contentful-page.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [
    NgClass,
    RouterModule,
    AppContentfulBiographyComponent,
    AppContentfulFAQLandingComponent,
    AppContentfulFAQComponent,
    AppContentfulLandingPageComponent,
    AppContentfulTitleComponent,
    AppContentfulArticleBreadcrumbComponent,
    AppContentfulCloudinaryImageComponent,
    AppContentfulRelatedProductsComponent,
    AppContentfulRelatedArticlesComponent,
    AppContentfulDescriptionComponent,
    AppContentfulMiniNavigationComponent,
    AppContentfulShareArticleComponent,
    AppContentfulRichTextBlockComponent,
  ],
})
export class AppContentfulPageComponent {
  contentfulSectionsContent!: ContentfulSectionContent[];
  desktopImage: ContentfulCloudinaryImage | undefined;
  mobileImage: ContentfulCloudinaryImage | undefined;
  FAQLandingContent: ContentfulFAQLandingPageContent | undefined;
  slugClass: string = '';
  articleCategory: ContentfulCategoryContent | undefined;
  hasCategory: boolean = false;
  private pendingTasks = inject(ExperimentalPendingTasks);
  private changeDetector = inject(ChangeDetectorRef);
  private logger = inject(GenericLogger);

  constructor(
    private metaService: MetaTagsService,
    private contentfulRenderService: ContentfulRenderService,
    @Optional() @Inject(PAGE_DATA) public componentData: BasePage,
  ) {
    this.renderContentfulData().catch((error) => {
      this.logger.error('Unexpected error', error);
    });
  }

  private async renderContentfulData() {
    const componentsReadyTask = this.pendingTasks.add();
    try {
      if (isContentfulPage(this.componentData)) {
        const content = await this.contentfulRenderService.getContentfulPageContent(
          this.componentData.extraData.routeParams,
          this.componentData.extraData.contentfulPagePathContent,
        );

        if (content) {
          this.processInputData(content);
        }

        this.changeDetector.detectChanges();
      }
    } catch (error) {
      this.logger.error('Error loading contentful page content', error);
    } finally {
      componentsReadyTask();
    }
  }

  private processInputData(currentValue: ContentfulPage): void {
    // Reset data on page redirect
    this.hasCategory = false;
    this.FAQLandingContent = undefined;
    this.desktopImage = undefined;
    this.mobileImage = undefined;
    this.contentfulSectionsContent = [];

    if (currentValue.extraData) {
      currentValue.extraData.info.forEach((contentfulSectionContent: ContentfulSectionContent) => {
        const slug = contentfulSectionContent.slug;
        const lastSegment = slug.split('/').pop();
        this.slugClass = lastSegment || '';

        const contentfulSection = contentfulSectionContent.contentfulContent?.content as any;
        if (!contentfulSection) {
          return;
        }
        if (contentfulSection.overview) {
          this.desktopImage = contentfulSection.overview.thumbnailImage;
          this.mobileImage = contentfulSection.overview.mobileThumbnail;
        }

        if (contentfulSection.type === 'faq-landing') {
          this.FAQLandingContent = contentfulSection;
        }

        if (contentfulSectionContent.contentType === 'articleOverview' && contentfulSection.category) {
          this.articleCategory = contentfulSection.category;
          this.hasCategory = true;
        }

        if (contentfulSection.meta) {
          this.setMetaTags(contentfulSection.meta);
        }
      });
      this.contentfulSectionsContent = currentValue.extraData.info;
    }
  }

  trackBySlug(index: number, contentfulSectionContent: ContentfulSectionContent) {
    return contentfulSectionContent.contentId;
  }

  setMetaTags(meta: MetaData) {
    this.metaService.setMetaTags({
      description: meta.fields.metaDescription || '',
      title: meta.fields.metaTitle || '',
      keywords: meta.fields.metaKeywords || '',
    });
  }
}
